import React, { lazy, Suspense, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import AppErrorBoundary from './components/AppErrorBoundary';
import PortfolioSiteRenderer from './components/PortfolioSiteRenderer';
import { PortfolioDataProvider } from './contexts/PortfolioDataContext';
import { getAdminMe } from './services/adminApi';

const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));

// Protected Route Component with proper redirect
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checkingAuth, setCheckingAuth] = React.useState(true);
  const [authorized, setAuthorized] = React.useState(false);
  
  useEffect(() => {
    let isMounted = true;

    getAdminMe()
      .then(() => {
        if (!isMounted) return;
        setAuthorized(true);
      })
      .catch(() => {
        if (!isMounted) return;
        setAuthorized(false);
        navigate('/admin/login', {
          replace: true,
          state: { from: location },
        });
      })
      .finally(() => {
        if (isMounted) {
          setCheckingAuth(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [navigate, location]);

  if (checkingAuth) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-cyan-400">Checking session...</div>;
  }

  if (!authorized) {
    return null;
  }

  return children;
};

// Login Route - redirect if already authenticated
const LoginRoute = ({ children }) => {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = React.useState(true);
  
  useEffect(() => {
    let isMounted = true;

    getAdminMe()
      .then(() => {
        if (isMounted) {
          navigate('/admin/dashboard', { replace: true });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) {
          setCheckingAuth(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  if (checkingAuth) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-cyan-400">Checking session...</div>;
  }

  return children;
};

function App() {
  useEffect(() => {
    // Add smooth scrolling behavior
    document.documentElement.style.scrollBehavior = 'smooth';
  }, []);

  return (
    <div className="App">
      <AppErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-950 text-cyan-400">Loading...</div>}>
          <Routes>
            {/* Public Portfolio Routes */}
            <Route path="/" element={
              <PortfolioDataProvider>
                <PortfolioSiteRenderer />
              </PortfolioDataProvider>
            } />

            {/* Admin Login Route */}
            <Route path="/admin/login" element={
              <LoginRoute>
                <AdminLogin />
              </LoginRoute>
            } />

            {/* Protected Admin Dashboard Route */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Redirect /admin to login */}
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <Toaster />
        </BrowserRouter>
      </AppErrorBoundary>
    </div>
  );
}

export default App;
